import { ApiActionList } from '../../config/ApiActionList';
import { communicationService } from '../../services';
import type {
  CreateChatroomRequest,
  CreateChatroomResponse,
  AddParticipantRequest,
  AddParticipantResponse,
  InboxItem,
  InboxRequest,
  LeaveConversationRequest,
  LeaveConversationResponse,
  MessagesResponse,
  ParticipantItem,
  PinChatRequest,
  PinChatResponse,
  RenameChatroomRequest,
  RenameChatroomResponse,
  StaffFacility,
  StaffParticipantsRequest,
  StaffParticipantsResponse,
  UpdateMessageStatusRequest,
  UpdateMessageStatusResponse,
  ValidateParticipantRequest,
  ValidateParticipantResponse,
} from './types';

export const chatApi = {
  // ── Inbox ──────────────────────────────────────────────────────────────────
  getInbox: async (request: InboxRequest): Promise<InboxItem[]> => {
    const action = ApiActionList.CHAT_FETCH_INBOX_MESSAGES;
    const res = await communicationService.post<InboxItem[], InboxRequest>(
      action.Url(),
      request,
      { action },
    );
    return res.data ?? [];
  },

  // ── Chatroom Messages ──────────────────────────────────────────────────────
  getChatroomMessages: async (
    chatroomId: string,
    lastMessageId = 0,
    messageFilter = 'all',
  ): Promise<MessagesResponse> => {
    const action = ApiActionList.CHAT_GET_CHATROOM_MESSAGES;
    const res = await communicationService.get<MessagesResponse>(
      action.Url(),
      { chatroomId, lastMessageId, messageFilter, action },
    );
    if (!res.data) throw new Error('No data returned from getChatroomMessages');
    return res.data;
  },

  // ── Chatroom Participants ──────────────────────────────────────────────────
  getChatroomParticipants: async (chatroomId: string): Promise<ParticipantItem[]> => {
    const action = ApiActionList.CHAT_GET_CHATROOM_PARTICIPANTS;
    const res = await communicationService.get<ParticipantItem[]>(
      action.Url(),
      { chatroomId, action },
    );
    return res.data ?? [];
  },

  // ── Update Message Status (mark as read) ──────────────────────────────────
  updateMessageStatus: async (chatRoomId: string): Promise<UpdateMessageStatusResponse> => {
    const action = ApiActionList.CHAT_UPDATE_MESSAGE_STATUS;
    const res = await communicationService.post<UpdateMessageStatusResponse, UpdateMessageStatusRequest>(
      action.Url(),
      { chatRoomId },
      { action },
    );
    if (!res.data) throw new Error('No data returned from updateMessageStatus');
    return res.data;
  },

  // ── Pin / Unpin Chatroom ─────────────────────────────────────────────
  pinChat: async (payload: PinChatRequest): Promise<PinChatResponse> => {
    const action = ApiActionList.PIN_CHAT;
    const res = await communicationService.post<PinChatResponse, PinChatRequest>(
      action.Url(),
      payload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from pinChat');
    return res.data;
  },

  // ── Leave Conversation ─────────────────────────────────────────────
  leaveConversation: async (payload: LeaveConversationRequest): Promise<LeaveConversationResponse> => {
    const action = ApiActionList.CHAT_LEAVE_CONVERSATION;
    const res = await communicationService.post<LeaveConversationResponse, LeaveConversationRequest>(
      action.Url(),
      payload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from leaveConversation');
    return res.data;
  },

  // ── Rename Chatroom ─────────────────────────────────────────────
  renameChatroom: async (payload: RenameChatroomRequest): Promise<RenameChatroomResponse> => {
    const action = ApiActionList.CHAT_RENAME_CHATROOM;
    // Mobile URL-encodes the name before sending (mirrors ChatService.renameChatRoom)
    const encodedPayload = { ...payload, chatRoomName: encodeURIComponent(payload.chatRoomName) };
    const res = await communicationService.post<RenameChatroomResponse, RenameChatroomRequest>(
      action.Url(),
      encodedPayload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from renameChatroom');
    return res.data;
  },

  // ── Staff Facilities ───────────────────────────────────────────────────────
  getStaffFacilities: async (): Promise<StaffFacility[]> => {
    const action = ApiActionList.GET_STAFF_FACILITIES;
    const res = await communicationService.get<StaffFacility[]>(
      action.Url(),
      { action },
    );
    return res.data ?? [];
  },

  // ── Staff Participants ─────────────────────────────────────────────────────
  getStaffParticipants: async (params: StaffParticipantsRequest): Promise<StaffParticipantsResponse> => {
    const action = ApiActionList.GET_STAFF_PARTICIPANTS;
    const queryParams: Record<string, unknown> = {
      facilityGuid: params.facilityGuid,
      limit: params.limit ?? 20,
      next: params.next ?? "",
      action,
    };
    if (params.q) queryParams.q = params.q;
    const res = await communicationService.get<StaffParticipantsResponse>(
      action.Url(),
      queryParams,
    );
    if (!res.data) throw new Error('No data returned from getStaffParticipants');
    return res.data;
  },

  // ── Validate Chat Participant ────────────────────────────────────────────────────
  validateChatParticipant: async (payload: ValidateParticipantRequest): Promise<ValidateParticipantResponse> => {
    const action = ApiActionList.VALIDATE_CHAT_PARTICIPANT;
    const res = await communicationService.post<ValidateParticipantResponse, ValidateParticipantRequest>(
      action.Url(),
      payload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from validateChatParticipant');
    return res.data;
  },

  // ── Create Chatroom ────────────────────────────────────────────────────────
  createChatroom: async (payload: CreateChatroomRequest): Promise<CreateChatroomResponse> => {
    const action = ApiActionList.CREATE_CHATROOM;
    const res = await communicationService.post<CreateChatroomResponse, CreateChatroomRequest>(
      action.Url(),
      payload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from createChatroom');
    return res.data;
  },
  // ── Add Chatroom Participant ───────────────────────────────────────────────
  addChatroomParticipant: async (payload: AddParticipantRequest): Promise<AddParticipantResponse> => {
    const action = ApiActionList.ADD_CHATROOM_PARTICIPANT;
    const res = await communicationService.post<AddParticipantResponse, AddParticipantRequest>(
      action.Url(),
      payload,
      { action },
    );
    if (!res.data) throw new Error('No data returned from addChatroomParticipant');
    return res.data;
  },};

